import React, { useState, useEffect } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { PatternFormat } from 'react-number-format'
import { useTranslation, Trans } from 'react-i18next'
import PropTypes from 'prop-types'
import { Button, Spinner, Alert } from 'react-bootstrap'
import { InputField } from 'reusableComponents/InputField'
import {
  resendOTPForNewUser,
  sendRecipientRegisterEmail,
  sendRecipientLoginEmail,
  verifyOTPWithEmail,
} from 'api/shipping'
import TermsAndConditions from 'components/TermsAndConditions'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import { breakpoints, colors } from 'globalStyles'
import Form from 'react-bootstrap/Form'
import FloatingLabel from 'react-bootstrap/FloatingLabel'
import { registerUser } from 'api/accounts'
import GuestButton from 'components/GuestButton'
import { maskEmail } from 'utils/helperFunctions'

/* eslint-disable no-useless-escape */
const EMAIL_REGEX =
  /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
/* eslint-enable no-useless-escape */

const Heading = styled.h4`
  text-align: center;
  display: inline;
  font-size: 25px;

  @media ${breakpoints.desktop} {
    color: ${colors.primary_blue};
    display: block;
    font-size: 16px;
    line-height: 22px;
    width: 140px;
    margin-bottom: 20px;
  }
`

const LoginLink = styled.a.attrs({
  id: 'register-link',
  rel: 'noreferrer',
  href: '#',
})`
  color: ${colors.primary_blue};
`

const ResendLink = styled.a.attrs((props) => ({
  id: 'resend-link',
  rel: 'noreferrer',
  href: props.disabled ? undefined : '#',
}))`
  color: ${colors.primary_blue};
  cursor: pointer;
  text-decoration: underline;

  ${({ disabled }) =>
    disabled &&
    `
      pointer-events: none;
      color: gray;
      cursor: not-allowed;
      text-decoration: none;
  `}
`

const StyledDiv = styled.div`
  font-size: 16px;
  line-height: 24px;
  text-align: center;
  margin-top: 24px;
`

const CustomInput = React.forwardRef((props, ref) => {
  const { t } = useTranslation('translation', { keyPrefix: 'login' })
  const { className, ...restProps } = props
  const isInvalid = props['aria-invalid'] === 'true' || props['aria-invalid'] === true
  return (
    <FloatingLabel
      {...restProps}
      ref={ref}
      label={
        <span>
          {t('mobile_number')}
          <span className="last-char">*</span>
        </span>
      }
    >
      <Form.Control
        {...restProps}
        ref={ref}
        className={`phoneInput ${className || ''}`.trim()}
        aria-invalid={isInvalid ? 'true' : 'false'}
        isInvalid={isInvalid}
      />
    </FloatingLabel>
  )
})

CustomInput.propTypes = {
  className: PropTypes.string,
  'aria-invalid': PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
}

CustomInput.defaultProps = {
  className: '',
  'aria-invalid': false,
}

const getVerificationCodeErrorMessage = (errorStatus, t) => {
  switch (errorStatus) {
    case 2:
      return t('subscription.verification_code_invalid_response')
    case 5:
      return t('forgotemail.verification_code_expired_error')
    case 6:
      return t('subscription.verification_code_max_attempts_error')
    default:
      return t('subscription.general_error_message')
  }
}

const StyledSection = styled.div`
  display: flex;
  flex-direction: column;
  text-align: center;
  margin: 20px auto;

  p {
    font-size: 16px;
    line-height: 24px;
    color: ${colors.secondary_grey};
    margin-bottom: 10px;
  }

  .subTitle {
    font-size: 20px;
    font-weight: bold;
    margin: 15px 0;
    line-height: 28px;
  }

  @media ${breakpoints.sm_above} {
    padding: 20px 0px;
    flex-direction: row;
    gap: 48px;

    .subTitle {
      font-size: 24px;
      line-height: 28px;
    }
    p {
      font-size: 18px;
      line-height: 28px;
    }
  }
`

const StyledVerifyContent = styled.div`
  font-family: Open Sans;
  line-height: 1.15;
  -webkit-text-size-adjust: 100%;
  -webkit-tap-highlight-color: rgba(17, 17, 17, 0);
  text-align: center;
  font-size: 1rem;
  line-height: 1.5;
  color: #212529;
  background-color: #fff;
  width: 350px;

  @media ${breakpoints.sm_above} {
    width: 670px;
  }

  form {
    width: 90%;
    text-align: left;
    margin: 24px auto;
  }

  label,
  .get-estimates-form label {
    display: inline-block;
    margin-bottom: 0.5rem;
  }

  .form-group {
    margin-top: 15px;
  }

  .form-group input:focus:not(.is-invalid):not([aria-invalid='true']) {
    border-radius: 4px;
    border: 2px solid ${colors.text_link_hover};
    outline: none;
  }

  .form-group input.is-invalid,
  .form-group input.is-invalid:focus,
  .form-group input[aria-invalid='true'],
  .form-group input[aria-invalid='true']:focus {
    border-radius: 4px;
    border: 1px solid ${colors.primary_red};
    outline: none;
    box-shadow: none;
  }

  .alert {
    padding: 1rem 0;
    color: #e00000;
  }

  .heading {
    color: ${colors.primary_blue};
    font-size: 2rem;
    width: 100%;
    font-weight: normal;
    padding-bottom: 24px;
  }

  input[type='text'],
  input[type='tel'] {
    border-radius: 5px;
    border-width: 3px;
    border-color: black;
    border-width: 1px;
    height: 3rem;
    text-align: left !important;
    padding-left: 1.25rem;
    padding-right: 1.25rem;
    width: 100%;
  }

  #OptInLabel {
    font-size: 0.8rem;
    position: relative;
  }
  .concents-checkbox {
    vertical-align: middle;
    margin-right: 0.5rem;
  }

  .form-control {
    padding: 0.5rem;
    display: block;
    width: 100%;
    height: calc(1.5em + 0.75rem + 2px);
    padding: 0.375rem 1.25rem;
    line-height: 1.5;
    color: #495057;
    background-color: #fff;
    background-clip: padding-box;
    border: 1px solid #ced4da;
    border-radius: 0.5rem;
    transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
  }

  .btn-primary {
    width: 100%;
    color: white;
    background-color: #001996;
    border-color: #001996;
  }

  .btn-secondary {
    width: 100%;
    color: #001996;
    background-color: #ffffff;
    border: 1px solid #001996;
    margin-top: 10px;

    @media ${breakpoints.sm_above} {
      margin-top: 0;
    }
  }

  .btn-secondary:hover {
    background-color: #ffffff;
    color: #0a38e6;
    border: 1px solid #0a38e6;
  }

  .btn-secondary:not(:disabled):not(.disabled):active {
    color: #001996;
    background-color: #ffffff;
    border: 2px solid #001996;
  }

  [type='submit']:not(:disabled) {
    cursor: pointer;
    -webkit-appearance: button;
  }
  .form-copy-error {
    color: #f00;
    border-color: #f00 !important;
  }
  .cta-box-col-2 div {
    max-width: 300px;
  }

  #firstname-error,
  #lastname-error,
  #phone-error,
  #optin-error {
    text-align: left;
    font-size: 11px;
    color: red;
  }
  .contacterror {
    color: #ff0004;
  }
  .contactnormal {
    color: #000000;
  }
  @media only screen and (min-width: 992px) {
    .cta-box-col-2 div {
      margin-left: 50px;
    }
  }
  .last-char {
    content: attr(data-last-char);
    /* modern browser */
    color: red !important;
  }
  .requiredFieldHeader {
    text-align: left;
    padding-left: 5%;
    font-size: 0.75rem;
    margin-top: 24px;
  }

  .registerButton {
    margin-top: 3%;
  }
`

const StyledGuestLink = styled.div`
  margin-top: 24px;
  font-size: 14px;
  line-height: 20px;
  text-align: center;
`

const RegisterType = {
  NEW: 'register-new',
  UPDATE: 'register-update',
}

export default () => {
  const { t, i18n } = useTranslation()
  const { language: lang } = i18n
  // 0: register, 1: register loading, 2: otp, 3: otp loading, 4: success,
  const [formStep, setFormStep] = useState(0)
  const navigate = useNavigate()
  // 0: no error, 1: send verification code error, 2: verify verification code error,
  // 3: subscribe error, 4: invalid phone number, 5: expired verification code, 6: maximum verification code attempts reached
  // 7: already registered error
  const [errorStatus, setErrorStatus] = useState(0)
  const [sendAgainDisabled, setSendAgainDisabled] = useState(false)
  const [verifyDisabled, setVerifyDisabled] = useState(false)
  const [isRegisterNew, setIsRegisterNew] = useState(true)
  const [prevLang, setPrevLang] = useState(i18n.language)

  const {
    control,
    getValues,
    setValue,
    watch,
    register,
    handleSubmit,
    formState: { errors, isSubmitted },
    trigger,
  } = useForm({
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  })

  const firstName = watch('firstName', '')
  const lastName = watch('lastName', '')
  const email = watch('email', '')
  const [phoneNumber, setPhoneNumber] = useState('')
  const [userId, setUserId] = useState('')
  const [oldUserId, setOldUserId] = useState('')
  const [isLoginEmail, setIsLoginEmail] = useState(false)

  useEffect(() => {
    if (sendAgainDisabled) {
      const zeroAttemptsTimeout = setTimeout(() => {
        setSendAgainDisabled(false)
        setErrorStatus(0)
      }, 1000 * 30)

      return () => {
        clearTimeout(zeroAttemptsTimeout)
      }
    }

    if (verifyDisabled) {
      const maxAttemptsTimeout = setTimeout(() => {
        setVerifyDisabled(false)
        setErrorStatus(0)
      }, 1000 * 30)

      return () => {
        clearTimeout(maxAttemptsTimeout)
      }
    }

    if (prevLang !== lang) {
      if (isSubmitted) {
        trigger()
      }
      setPrevLang(lang)
    }
    return null
  }, [sendAgainDisabled, verifyDisabled, prevLang, lang, trigger, isSubmitted])

  const onClickLogin = (e) => {
    e.preventDefault()
    navigate(`/rs/tr/${lang}/login`, { replace: true })
  }

  const maskNumber = (num) => {
    const cleaned = num.replace(/[^\d]/g, '')
    const withoutCountryCode = cleaned.startsWith('1') ? cleaned.slice(1) : cleaned
    const masked = withoutCountryCode.replace(/^(\d{3})(\d{3})(\d{4})$/, '***-***-$3')
    return masked
  }

  const formatNumber = (num) => {
    return num.replace(/[^\d]/g, '')
  }

  const sendCode = async (values) => {
    const formattedNumber = formatNumber(values.phoneNumber)
    setPhoneNumber(values.phoneNumber)

    const userObject = {
      lang: i18n.language,
      phoneNumber: formattedNumber,
      emailAddress: values.email,
      optIn: values.optIn,
      firstName: values.firstName,
      lastName: values.lastName,
    }
    try {
      setFormStep(1)
      const registerResponse = await registerUser(userObject)
      const parsedResponse = registerResponse.data
      const { responseCode } = parsedResponse

      if (responseCode === 'SUCCESS') {
        setUserId(parsedResponse.userId)
        setFormStep(2)
        setIsRegisterNew(true)
        setIsLoginEmail(false)
      } else if (responseCode === 'PHONE_EXISTS' || responseCode === 'EMAIL_EXISTS') {
        setUserId(parsedResponse.userId)
        setOldUserId(parsedResponse.oldUserId)
        setFormStep(2)
        setIsRegisterNew(false)
        setIsLoginEmail(false)
      } else if (responseCode === 'PHONE_AND_EMAIL_EXISTS') {
        setFormStep(4)
        setIsLoginEmail(true)

        const emailAddress = getValues('email')
        const { language } = i18n

        sendRecipientLoginEmail(emailAddress, language)
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('registerResponse Error:', err)
    }
  }

  const onSubscribe = () => {
    const emailAddress = getValues('email')
    const { language } = i18n
    const type = isRegisterNew ? RegisterType.NEW : RegisterType.UPDATE

    sendRecipientRegisterEmail(emailAddress, language, type, userId, oldUserId)
      .then((result) => {
        if (result.status === 200) {
          setFormStep(4)
          return true
        }
        setErrorStatus(3)
        setFormStep(2)
        return true
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.error('verification err', err)
        setErrorStatus(3)
        setFormStep(2)
      })
  }

  const onClickResendEmail = (e) => {
    e.preventDefault()
    setSendAgainDisabled(true)
    const emailAddress = getValues('email')
    const { language } = i18n

    if (isLoginEmail) {
      sendRecipientLoginEmail(emailAddress, language)
    } else {
      const type = isRegisterNew ? RegisterType.NEW : RegisterType.UPDATE
      sendRecipientRegisterEmail(emailAddress, language, type, userId, oldUserId)
    }
    // eslint-disable-next-line no-console
    console.log('Resend email verification')
  }

  const verifyCode = (values) => {
    // Template syntax follows url-template https://www.npmjs.com/package/url-template
    const verificationcode = values.verificationCode
    const prn = `PYWWeb:firstname.lastname:${i18n.language}`

    setFormStep(3)

    const args = [formatNumber(phoneNumber), lang, verificationcode, prn, email, userId]

    if (oldUserId) {
      args.push(oldUserId)
    }

    verifyOTPWithEmail(...args)
      .then((result) => {
        if (
          result.status === 200 &&
          result.data.message === 'Phone verification and update successful'
        ) {
          onSubscribe()
          return true
        }

        return true
      })
      .catch((err) => {
        if (err.status === 400 && typeof err.response?.data?.error !== 'undefined') {
          const response = err.response.data.error

          if (response.response === 'VERIFICATION_CODE_DOES_NOT_MATCH') {
            setErrorStatus(2)
            setFormStep(2)
            return null
          }
          if (response.response === 'VERIFICATION_CODE_DOES_NOT_EXISTS') {
            setErrorStatus(5)
            setFormStep(2)
            return null
          }
          if (response.response === 'MAX_VERIFICATIONCODE_VERIFICATION_ATTEMPTS') {
            setErrorStatus(6)
            setFormStep(2)
            setVerifyDisabled(true)
            return null
          }

          setErrorStatus(7)
          setFormStep(2)
          return null
        }
        // eslint-disable-next-line no-console
        console.error('Failed to connect to API', err)
        setErrorStatus(3)
        setFormStep(2)
        return null
      })
  }

  const resendCodeHandler = (e) => {
    e.preventDefault()
    setSendAgainDisabled(true)
    resendOTPForNewUser(formatNumber(getValues('phoneNumber')), lang)
      .then((result) => {
        if (result.status === 200 && typeof result.data.data !== 'undefined') {
          if (
            result.data.data.length > 0 &&
            result.data.data[0].response === 'MAX_VERIFICATIONCODE_RESEND_LIMIT'
          ) {
            setErrorStatus(6)
            setFormStep(2)
            setSendAgainDisabled(true)
            return true
          }
        }
        return true
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.error('Failed to connect to API', err)
      })
  }

  const submitForm = async (fields) => {
    switch (formStep) {
      case 0:
        await sendCode(fields)
        break
      case 2:
        verifyCode(fields)
        break
      default:
        break
    }
  }

  const backButtonHandler = () => {
    setFormStep(0)
    // clear the verification code field
    setValue('verificationCode', '')
    setOldUserId('')
    setUserId('')
  }

  return (
    <StyledVerifyContent>
      <Heading className="heading">{t('subscription.create_account')}</Heading>
      {(formStep === 0 || formStep === 1) && (
        <h6 className="requiredFieldHeader">
          <span className="last-char">*</span> {t('subscription.required_field')}
        </h6>
      )}
      <form onSubmit={handleSubmit(submitForm)}>
        {(formStep === 0 || formStep === 1) && (
          <>
            <div className="form-group">
              <InputField
                id="firstNameField"
                value={firstName}
                type="text"
                name="firstName"
                label={t('subscription.first_name')}
                aria-invalid={errors.firstName ? 'true' : 'false'}
                isRequired
                {...register('firstName', {
                  required: {
                    value: true,
                    message: t('subscription.first_name_empty_error'),
                  },
                  pattern: {
                    value:
                      /^([a-zA-ZàâäçéèêëîïôöùûüÿñæœÀÂÄÇÉÈÊËÎÏÔÖÙÛÜŸÑÆŒ]+[\s-])*[a-zA-ZàâäçéèêëîïôöùûüÿñæœÀÂÄÇÉÈÊËÎÏÔÖÙÛÜŸÑÆŒ]+$/,
                    message: t('subscription.first_name_invalid_error'),
                  },
                  maxLength: {
                    value: 50,
                    message: t('subscription.first_name_invalid_error'),
                  },
                  setValueAs: (value) => value?.trim(),
                })}
              />
              <span role="alert" id="firstname-error">
                {errors.firstName?.message}
              </span>
            </div>
            <div className="form-group">
              <InputField
                id="lastNameField"
                value={lastName}
                type="text"
                name="lastName"
                aria-invalid={errors.lastName ? 'true' : 'false'}
                isRequired
                label={t('subscription.last_name')}
                {...register('lastName', {
                  required: {
                    value: true,
                    message: t('subscription.last_name_empty_error'),
                  },
                  maxLength: {
                    value: 50,
                    message: t('subscription.last_name_invalid_error'),
                  },
                  pattern: {
                    value:
                      /^([a-zA-ZàâäçéèêëîïôöùûüÿñæœÀÂÄÇÉÈÊËÎÏÔÖÙÛÜŸÑÆŒ]+[\s-])*[a-zA-ZàâäçéèêëîïôöùûüÿñæœÀÂÄÇÉÈÊËÎÏÔÖÙÛÜŸÑÆŒ]+$/,
                    message: t('subscription.last_name_invalid_error'),
                  },
                  setValueAs: (value) => value?.trim(),
                })}
              />
              <span role="alert" id="lastname-error">
                {errors.lastName?.message}
              </span>
            </div>
            <div className="form-group">
              <Controller
                control={control}
                name="phoneNumber"
                label={t('login.mobile_number')}
                defaultValue=""
                rules={{
                  required: {
                    value: true,
                    message: t('subscription.phone_number_invalid_error'),
                  },
                  validate: (value) => {
                    const cleanedValue = value?.trim().replace(/\D/g, '')
                    return (
                      cleanedValue.length === 11 || t('subscription.phone_number_invalid_error')
                    )
                  },
                  setValueAs: (value) => value?.trim(),
                }}
                placeholder="Phone"
                render={({ field: { name, value, ref, onChange }, fieldState: { invalid } }) => (
                  <PatternFormat
                    format="1 ### ### ####"
                    displayType="input"
                    type="tel"
                    name={name}
                    value={value}
                    getInputRef={ref}
                    onChange={onChange}
                    customInput={CustomInput}
                    className={invalid ? 'is-invalid' : ''}
                    aria-invalid={invalid ? 'true' : 'false'}
                    placeholder="Phone Number"
                  />
                )}
              />
              <span role="alert" id="phone-error">
                {errors.phoneNumber?.message}
              </span>
            </div>
            <div className="form-group">
              <InputField
                id="emailField"
                value={email}
                type="text"
                name="email"
                aria-invalid={errors.email ? 'true' : 'false'}
                isRequired
                label={t('subscription.email')}
                {...register('email', {
                  required: {
                    value: true,
                    message: t('subscription.email_invalid_error'),
                  },
                  maxLength: {
                    value: 50,
                    message: t('subscription.email_invalid_error'),
                  },
                  pattern: {
                    value: EMAIL_REGEX,
                    message: t('subscription.email_invalid_error'),
                  },
                  setValueAs: (value) => value?.trim(),
                })}
              />
              <span role="alert" id="phone-error">
                {errors.email?.message}
              </span>
            </div>
            <div className="form-group">
              <label id="OptInLabel" htmlFor="optin">
                <input
                  type="checkbox"
                  name="optIn"
                  value="true"
                  className="concents-checkbox"
                  aria-invalid={errors.optIn ? 'true' : 'false'}
                  {...register('optIn', {
                    required: {
                      value: true,
                      message: t('subscription.concents_empty_error'),
                    },
                  })}
                />
                <Trans
                  t={t}
                  i18nKey="subscription.concents_message"
                  components={[
                    <a
                      href={
                        i18n.language === 'en'
                          ? process.env.REACT_APP_PUROLATOR_PRIVACY_POLICY_URL
                          : process.env.REACT_APP_PUROLATOR_PRIVACY_POLICY_URL_FR
                      }
                      target="_blank"
                      rel="noreferrer"
                    >
                      {t('subscription.privacy_policy_text')}
                    </a>,
                    <TermsAndConditions />,
                  ]}
                />
              </label>
              <span role="alert" id="optin-error">
                {errors.optIn?.message}
              </span>
              {/* {errorStatus === 1 ? (
                <Alert variant="light">{t('subscription.general_error_message')}</Alert>
              ) : null}
              {errorStatus === 4 ? (
                <Alert variant="light">{t('subscription.phone_number_invalid_error')}</Alert>
              ) : null}
              {errorStatus === 7 ? (
                <Alert variant="light">{t('subscription.phone_number_already_registered')}</Alert>
              ) : null} */}
            </div>
          </>
        )}
        {(formStep === 2 || formStep === 3) && (
          <>
            <div className="row">
              <div className="col" style={{ textAlign: 'center' }}>
                <Trans
                  t={t}
                  i18nKey="subscription.verification_code_message"
                  values={{ phoneNumber: maskNumber(getValues('phoneNumber')) }}
                  components={{ phoneComp: <b /> }}
                />
              </div>
            </div>
            <div className="row">
              <div className="form-group">
                <input
                  type="text"
                  name="verificationCode"
                  placeholder={t('subscription.verification_code_empty_error')}
                  aria-invalid={errors.verificationCode ? 'true' : 'false'}
                  style={{ paddingLeft: '10px' }}
                  {...register('verificationCode', {
                    required: {
                      value: true,
                      message: t('subscription.verification_code_empty_error'),
                    },
                    pattern: {
                      value: /^[0-9]*$/,
                      message: t('subscription.verification_code_invalid_error'),
                    },
                    maxLength: {
                      value: 6,
                      message: t('subscription.verification_code_invalid_error'),
                    },
                    minLength: {
                      value: 6,
                      message: t('subscription.verification_code_invalid_error'),
                    },
                  })}
                />
                <div id="firstname-error">{errors.verificationCode?.message}</div>
              </div>
            </div>
            <StyledDiv>
              <Trans
                t={t}
                i18nKey="subscription.no_sms_received"
                components={[
                  <ResendLink onClick={resendCodeHandler} disabled={sendAgainDisabled} />,
                ]}
              />
            </StyledDiv>

            {errorStatus !== 0 ? (
              <Alert variant="light">{getVerificationCodeErrorMessage(errorStatus, t)}</Alert>
            ) : null}
          </>
        )}
        {formStep === 4 && (
          <section>
            <div className="row">
              <div className="col" style={{ textAlign: 'center', marginTop: '24px' }}>
                <h4>{t('subscription.almost_done')}</h4>
              </div>
            </div>
            <div className="row">
              <div className="col" style={{ textAlign: 'center', marginTop: '24px' }}>
                <Trans
                  t={t}
                  i18nKey="subscription.verify_email_message"
                  values={{ email: maskEmail(getValues('email')) }}
                  components={{ emailComp: <span style={{ color: colors.primary_blue }} /> }}
                />
              </div>
            </div>
            <div className="row">
              <div className="col" style={{ textAlign: 'center', marginTop: '24px' }}>
                <Trans
                  t={t}
                  i18nKey="login.no_email_received"
                  components={[
                    <ResendLink onClick={onClickResendEmail} disabled={sendAgainDisabled} />,
                  ]}
                />
              </div>
            </div>
          </section>
        )}
        {(formStep === 0 || formStep === 1) && (
          <>
            <Button type="submit" className="btn-primary registerButton" disabled={verifyDisabled}>
              {formStep === 0 ? (
                t('subscription.register_button')
              ) : (
                <Spinner as="span" animation="border" size="md" role="status" aria-hidden="true" />
              )}
            </Button>
            <StyledDiv>
              <Trans
                t={t}
                i18nKey="subscription.have_account"
                components={[<LoginLink onClick={onClickLogin} />]}
              />
            </StyledDiv>
          </>
        )}
        {(formStep === 2 || formStep === 3) && (
          <StyledSection>
            <Button className="btn-secondary" disabled={verifyDisabled} onClick={backButtonHandler}>
              {t('forgotemail.go_back')}
            </Button>
            <Button type="submit" className="btn-primary" disabled={verifyDisabled}>
              {formStep === 2 ? (
                t('subscription.verify_button')
              ) : (
                <Spinner as="span" animation="border" size="md" role="status" aria-hidden="true" />
              )}
            </Button>
          </StyledSection>
        )}
        <StyledGuestLink>
          <GuestButton isSrcLandingPage={false} />
        </StyledGuestLink>
      </form>
    </StyledVerifyContent>
  )
}
